import React, { FC, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { get_especialidades } from "../services/api";
import { FormattedAsignatura } from "../interfaces/ObjetivosAprendizaje/iAsignaturas";
import { useAuthContext } from "../hooks/Auth";
import SpinnerUmmIA from "../components/Loading/SpinnerUmmIA";

interface Props {
  selectedEspecialidadId: string;
  onEspecialidadChange: (especialidad: FormattedAsignatura) => void;
  disabled?: boolean;
}

const GetEspecialidad: FC<Props> = ({
  selectedEspecialidadId,
  onEspecialidadChange,
  disabled = false,
}) => {
  const { user } = useAuthContext();
  const navigate = useNavigate();

  const allowedIds = Array.isArray(user?.especialidades)
    ? user?.especialidades
    : undefined;

  const [data, setData] = useState<FormattedAsignatura[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchEspecialidades = async () => {
      setLoading(true);
      try {
        const resp = await get_especialidades();

        if (resp?.status === 200) {
          const raw = Array.isArray(resp.data?.data) ? resp.data.data : [];
          const especialidades: FormattedAsignatura[] = raw.map((e: any) => ({
            id: e.id_especialidad,
            nombre: e.name,
          }));
          setData(especialidades);
        } else {
          console.error("get_especialidades: respuesta inválida", resp);
          setData([]);
        }
      } catch (error) {
        console.error("Error consultando especialidades:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEspecialidades();
  }, []);

  const allowedSet = useMemo(() => new Set(allowedIds ?? []), [allowedIds]);

  const visibleData = useMemo(() => {
    if (allowedIds === undefined) return data;
    if (allowedIds.length === 0) return [];
    return data.filter((esp) => allowedSet.has(esp.id));
  }, [data, allowedIds, allowedSet]);

  const hasNoSelectedEspecialidades = !loading && visibleData.length === 0;

  useEffect(() => {
    if (!selectedEspecialidadId) return;

    const stillValid = visibleData.some((x) => x.id === selectedEspecialidadId);
    if (!stillValid && visibleData.length === 1) {
      onEspecialidadChange(visibleData[0]);
    }
  }, [selectedEspecialidadId, visibleData, onEspecialidadChange]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const selectedId = event.target.value;
    const seleccion = visibleData.find((item) => item.id === selectedId);
    if (seleccion) onEspecialidadChange(seleccion);
  };

  const handleGoToUserProfile = () => {
  navigate("/asistente/userprofile", {
    state: { scrollToEspecialidades: true },
  });
};

  const renderValue = (value: string) => {
    if (!value) return "Selecciona una especialidad";
    const found = visibleData.find((x) => x.id === value);
    return found?.nombre ?? "Especialidad";
  };

  return (
    <Grid item xs={12}>
      <Box>
        <Typography
          sx={{
            fontFamily: "montserrat",
            fontSize: "1.25rem",
            fontWeight: "bold",
            color: "#336B87",
          }}
        >
          Especialidad *
        </Typography>

        {!hasNoSelectedEspecialidades && (
          <FormControl fullWidth disabled={disabled}>
            <Select
              labelId="select-especialidad"
              id="select-especialidad"
              variant="outlined"
              value={selectedEspecialidadId || ""}
              displayEmpty
              onChange={handleChange}
              renderValue={renderValue}
              sx={{
                fontFamily: "montserrat",
                fontSize: "14px",
                borderRadius: 2,
                "& .MuiOutlinedInput-root": { borderRadius: 2 },
                "& fieldset": { borderColor: "#BDBDBD" },
                "&:hover fieldset": { borderColor: "#9E9E9E" },
                "&.Mui-focused fieldset": { borderColor: "#005559" },
                "& .MuiSelect-select": {
                  color: selectedEspecialidadId ? "#005559" : "#B3D1D1",
                },
              }}
            >
              <MenuItem value="" disabled>
                {loading ? "Cargando..." : "Selecciona una especialidad"}
              </MenuItem>

              {loading && (
                <MenuItem value="" disabled>
                  <Box display="flex" alignItems="center" gap={1}>
                    <SpinnerUmmIA logoWidth={20} logoHeight={20} />
                    Cargando...
                  </Box>
                </MenuItem>
              )}

              {visibleData.map((esp) => (
                <MenuItem key={esp.id} value={esp.id} sx={{ color: "#336B87" }}>
                  <Typography
                    sx={{
                      fontFamily: "montserrat",
                      fontSize: "0.8rem",
                      color: "#336B87",
                    }}
                  >
                    {esp.nombre}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {hasNoSelectedEspecialidades && (
          <Box
            sx={{
              p: 2,
              border: "1px solid #D6E4EA",
              borderRadius: 2,
              backgroundColor: "#F8FBFC",
            }}
          >
            <Typography
              sx={{
                fontFamily: "montserrat",
                fontSize: "0.92rem",
                color: "#336B87",
                mb: 1,
              }}
            >
              No tienes especialidades seleccionadas.
            </Typography>

            <Button
              variant="contained"
              onClick={handleGoToUserProfile}
              sx={{
                textTransform: "none",
                fontFamily: "montserrat",
                fontWeight: 700,
                backgroundColor: "#336B87",
                "&:hover": {
                  backgroundColor: "#28566d",
                },
              }}
            >
              Ir a configurar especialidades
            </Button>
          </Box>
        )}
      </Box>
    </Grid>
  );
};

export default GetEspecialidad;